import { Button, Col, Form, Input, Row, Typography, Upload, message } from "antd";
import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";

import { DocumentLinkEntry, InlineEdit, documentLinkErrors } from "../../Constants";
import styles from "../../WorkOrder.module.scss";

interface Props {
  files: File[];
  /* The updater form matters: antd calls beforeUpload once per file, so a
     multi-file drop would otherwise have every call read the same stale list
     and keep only the last file. */
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  savedDocuments?: any[];
  setSavedDocuments?: React.Dispatch<React.SetStateAction<any[]>>;
  links: DocumentLinkEntry[];
  setLinks: (links: DocumentLinkEntry[]) => void;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  isSavingDraft?: boolean;
  inlineEdit?: InlineEdit;
  /** Documents the work order already holds (a reopened draft) — they count
      towards the one document Next needs. */
  savedCount?: number;
}

const MAX_FILE_SIZE_MB = 20;
const MAX_FILES = 10;

/**
 * Give a repeat upload a numbered name: `report.pdf`, `report (1).pdf`, ...
 *
 * `File.name` is read-only, so the file is rewrapped. Counting up past names
 * already taken means removing `(1)` and re-adding the same file lands back on
 * `(1)` rather than skipping to `(2)`.
 */
const withUniqueName = (file: File, existing: File[]): File => {
  const taken = new Set(existing.map((entry) => entry.name));
  if (!taken.has(file.name)) return file;

  const dot = file.name.lastIndexOf(".");
  const stem = dot === -1 ? file.name : file.name.slice(0, dot);
  const extension = dot === -1 ? "" : file.name.slice(dot);

  let counter = 1;
  let candidate = `${stem} (${counter})${extension}`;
  while (taken.has(candidate)) {
    counter += 1;
    candidate = `${stem} (${counter})${extension}`;
  }
  return new File([file], candidate, { type: file.type, lastModified: file.lastModified });
};

export const blankLink = (seed: number): DocumentLinkEntry => ({
  key: `link-${seed}`,
  documentName: "",
  documentLink: "",
});

/**
 * Step 3 — Project Documents.
 *
 * Files are held in wizard state and submitted with the work order as
 * multipart parts; nothing is uploaded here. The first selected PDF is
 * previewed inline through the shared PDFView (same renderer as the drilldown
 * viewer) with its own zoom controls.
 *
 * "Additional Document Details" captures links to documents that live
 * elsewhere — name + URL pairs, repeatable.
 */
export const ProjectDocuments = ({
  files,
  setFiles,
  savedDocuments = [],
  setSavedDocuments,
  links,
  setLinks,
  onBack,
  onNext,
  onSaveDraft,
  isSavingDraft,
  inlineEdit,
  savedCount = 0,
}: Props) => {
  const totalCount = files.length + (savedDocuments.length || savedCount);
  const needsDocument = !inlineEdit && totalCount === 0;

  const [activePreview, setActivePreview] = useState<{ name: string; url: string; revoke?: boolean } | null>(null);

  /* Object URLs must be revoked or the blobs leak for the page's lifetime. */
  useEffect(() => {
    return () => {
      if (activePreview?.revoke) URL.revokeObjectURL(activePreview.url);
    };
  }, [activePreview]);

  const onSelectFile = (index: number) => {
    const file = files[index];
    if (!file) return;
    if (activePreview?.revoke) URL.revokeObjectURL(activePreview.url);
    const url = URL.createObjectURL(file);
    setActivePreview({ name: file.name, url, revoke: true });
  };

  const onSelectSavedDoc = (doc: any) => {
    if (activePreview?.revoke) URL.revokeObjectURL(activePreview.url);
    setActivePreview({ name: doc.name, url: doc.url, revoke: false });
  };

  const beforeUpload = (file: File) => {
    if (file.type !== "application/pdf") {
      message.error("Only PDF files are allowed");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      message.error(`Maximum file size is ${MAX_FILE_SIZE_MB} MB`);
      return Upload.LIST_IGNORE;
    }

    setFiles((current) => {
      if (current.length + savedDocuments.length >= MAX_FILES) {
        message.error(`Maximum ${MAX_FILES} files`);
        return current;
      }
      return [...current, withUniqueName(file, current)];
    });
    return Upload.LIST_IGNORE; // held locally; submitted with the work order
  };

  const removeFile = (index: number) => {
    const removed = files[index];
    const remaining = files.filter((_, i) => i !== index);
    setFiles(remaining);
    if (activePreview && activePreview.name === removed?.name) {
      if (activePreview.revoke) URL.revokeObjectURL(activePreview.url);
      setActivePreview(null);
    }
  };

  const removeSavedDoc = (index: number) => {
    const removed = savedDocuments[index];
    const remaining = savedDocuments.filter((_, i) => i !== index);
    setSavedDocuments?.(remaining);
    if (activePreview && (activePreview.name === removed?.name || activePreview.url === removed?.url)) {
      if (activePreview.revoke) URL.revokeObjectURL(activePreview.url);
      setActivePreview(null);
    }
  };

  /* Repeated names / links and broken links — the rules the detail page's
     Edit Additional Document Link applies too. */
  const rowErrors = documentLinkErrors(links);
  const nameError = (link: DocumentLinkEntry) => rowErrors[link.key]?.name;
  const linkError = (link: DocumentLinkEntry) => rowErrors[link.key]?.link;
  /* Nothing moves on — Next, Save, Save as draft, Add Another — while a row is wrong. */
  const hasLinkErrors = Object.keys(rowErrors).length > 0;
  /* A row needs its link before another can be added. */
  const canAddLink = links.every((link) => String(link.documentLink ?? "").trim()) && !hasLinkErrors;

  const updateLink = (key: string, patch: Partial<DocumentLinkEntry>) =>
    setLinks(links.map((link) => (link.key === key ? { ...link, ...patch } : link)));

  return (
    <>
      <Typography.Title level={4}>Project Documents</Typography.Title>

      <div className={styles.fileRow}>
        <Icon
          icon="vscode-icons:file-type-pdf2"
          className={styles.fileIcon}
        />
        <Typography.Text>Please upload your files</Typography.Text>
      </div>

      <Row gutter={24}>
        <Col
          xs={24}
          lg={12}
        >
          <Upload.Dragger
            multiple
            accept="application/pdf"
            beforeUpload={beforeUpload}
            showUploadList={false}
          >
            <p style={{ fontSize: 32, margin: 0 }}>
              <Icon icon="mdi:cloud-upload-outline" />
            </p>
            <Typography.Paragraph style={{ marginBottom: 4 }}>
              Only PDF files are allowed
            </Typography.Paragraph>
            <Typography.Text type="danger">
              (Maximum file Size : {MAX_FILE_SIZE_MB} MB)
            </Typography.Text>
            <br />
            <Typography.Text type="danger">(Maximum Files : {MAX_FILES})</Typography.Text>
            <Typography.Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
              <a>Click</a> or <a>drag and drop</a> file to upload
            </Typography.Paragraph>
          </Upload.Dragger>
        </Col>

        <Col
          xs={24}
          lg={12}
        >
          {savedDocuments?.map((doc: any, index: number) => (
            <div
              className={styles.fileRow}
              key={doc.key ?? `saved-${index}`}
            >
              <Icon
                icon="vscode-icons:file-type-pdf2"
                className={styles.fileIcon}
                style={{ cursor: "pointer" }}
                onClick={() => onSelectSavedDoc(doc)}
              />
              <Typography.Text
                style={{ cursor: "pointer" }}
                onClick={() => onSelectSavedDoc(doc)}
              >
                {doc.name}
              </Typography.Text>
              <Icon
                icon="fluent:delete-48-regular"
                className={styles.viewIcon}
                onClick={() => removeSavedDoc(index)}
              />
            </div>
          ))}

          {files.map((file, index) => (
            <div
              className={styles.fileRow}
              key={`${file.name}-${index}`}
            >
              <Icon
                icon="vscode-icons:file-type-pdf2"
                className={styles.fileIcon}
                style={{ cursor: "pointer" }}
                onClick={() => onSelectFile(index)}
              />
              <Typography.Text
                style={{ cursor: "pointer" }}
                onClick={() => onSelectFile(index)}
              >
                {file.name}
              </Typography.Text>
              <Icon
                icon="fluent:delete-48-regular"
                className={styles.viewIcon}
                onClick={() => removeFile(index)}
              />
            </div>
          ))}
        </Col>
      </Row>

      {/* Inline preview of the selected PDF. */}
      {!activePreview?.url && (
        <div style={{ marginTop: 24 }}>
          <div className={styles.documentPreviewEmpty}>
            <Icon
              icon="ph:file-pdf"
              inline
              fontSize={24}
            />
            <span>No documents to preview</span>
          </div>
        </div>
      )}

      {activePreview?.url && (
        <div style={{ marginTop: 24 }}>
          <div className={styles.fileRow}>
            <Icon
              icon="vscode-icons:file-type-pdf2"
              className={styles.fileIcon}
            />
            <Typography.Text>{activePreview.name}</Typography.Text>
          </div>
          <iframe
            className={styles.documentPreview}
            src={activePreview.url}
            title={activePreview.name}
          />
        </div>
      )}

      <Typography.Title
        level={4}
        style={{ marginTop: 24 }}
      >
        Additional Document Details
      </Typography.Title>
      <div className={styles.fileRow}>
        <Icon
          icon="vscode-icons:file-type-pdf2"
          className={styles.fileIcon}
        />
        <Typography.Text>Enter your document details</Typography.Text>
      </div>

      <Form layout="vertical">
        {links.map((link) => (
          <Row
            gutter={24}
            key={link.key}
            align="top"
          >
            <Col
              xs={24}
              sm={10}
            >
              <Form.Item
                label="Document Name"
                validateStatus={nameError(link) ? "error" : undefined}
                help={nameError(link)}
              >
                <Input
                  value={link.documentName}
                  onChange={(e) => updateLink(link.key, { documentName: e.target.value })}
                />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={10}
            >
              <Form.Item
                label="Document Link"
                validateStatus={linkError(link) ? "error" : undefined}
                help={linkError(link)}
              >
                <Input
                  value={link.documentLink}
                  onChange={(e) => updateLink(link.key, { documentLink: e.target.value })}
                />
              </Form.Item>
            </Col>
            <Col>
              <Icon
                icon="fluent:delete-48-regular"
                className={styles.adminRowDelete}
                onClick={() =>
                  // Keep one row so the section never renders empty.
                  setLinks(
                    links.length === 1 ? [blankLink(0)] : links.filter((l) => l.key !== link.key)
                  )
                }
              />
            </Col>
          </Row>
        ))}

        <Row justify="end">
          <Col>
            <span
              className={canAddLink ? styles.addAnother : styles.addAnotherDisabled}
              onClick={() => canAddLink && setLinks([...links, blankLink(links.length + 1)])}
            >
              <Icon
                icon="akar-icons:plus"
                inline
              />
              Add Another
            </span>
          </Col>
        </Row>
      </Form>

      <Row
        justify="end"
        gutter={16}
        className={styles.stepActions}
      >
        {inlineEdit ? (
          <Col>
            <Button onClick={inlineEdit.onCancel}>Cancel</Button>
          </Col>
        ) : (
          <>
            <Col>
              <Button onClick={onBack}>Back</Button>
            </Col>
            <Col>
              <Button
                loading={isSavingDraft}
                disabled={hasLinkErrors}
                onClick={onSaveDraft}
              >
                Save as draft
              </Button>
            </Col>
          </>
        )}
        <Col>
          {/* At least one project document before moving on. */}
          <Button
            type="primary"
            loading={inlineEdit?.isSaving}
            disabled={needsDocument || hasLinkErrors}
            title={
              needsDocument
                ? "Upload at least one document to continue"
                : hasLinkErrors
                ? "Correct the document details to continue"
                : undefined
            }
            onClick={onNext}
          >
            {inlineEdit ? "Save" : "Next"}
          </Button>
        </Col>
      </Row>
    </>
  );
};
